import React, { useMemo, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row } from 'react-bootstrap';

const photoUrl = (token, id, size) => `/api/public/catalog/${encodeURIComponent(token)}/images/${id}?size=${size}`;

/** A product's photos as addresses: the CDN's for one in the media bucket, this page's own for one still in the database. */
const photosOf = (p, token) => (p.photoSources && p.photoSources.length > 0
  ? p.photoSources.map((s) => ({ id: s.id, thumb: s.thumb || photoUrl(token, s.id, 'thumb'), full: s.full || photoUrl(token, s.id, 'full') }))
  : (p.photos || []).map((id) => ({ id, thumb: photoUrl(token, id, 'thumb'), full: photoUrl(token, id, 'full') })));

/** The number WhatsApp wants: digits with the country code (a 10-digit Indian number gets 91 in front); empty when there is none to use. */
export function whatsappNumber(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '').replace(/^0+/, '');
  if (digits.length === 10) return `91${digits}`;
  return digits.length >= 11 ? digits : '';
}

/**
 * The product catalog a business sends: a search box, category buttons, and a card per product with its photo, price (and the GST on top)
 * and whether it is in stock, as the business chose. A product can be asked about on WhatsApp in one tap. It prints as a plain list.
 */
function CatalogView({ data, token, money }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [open, setOpen] = useState(null); // the product whose photos are shown large
  const [shown, setShown] = useState(0); // which of its photos
  const phone = whatsappNumber(data.organization?.phone);

  const products = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return (data.products || []).filter((p) => {
      if (category && p.category !== category) return false;
      const hay = `${p.name} ${p.brand || ''} ${p.category || ''} ${p.sku || ''} ${p.description || ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [data.products, search, category]);

  const ask = (p) => `https://wa.me/${phone}?text=${encodeURIComponent(`Hello, I am interested in ${p.name}${p.sku ? ` (${p.sku})` : ''} from your catalog.`)}`;

  return (
    <div data-testid="catalog-view">
      <style>{`
        .catalog-card { break-inside: avoid; }
        @media print {
          .catalog-card { border: 1px solid #ccc !important; }
          .catalog-col { flex: 0 0 33.333% !important; max-width: 33.333% !important; }
        }
      `}</style>
      <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-2">
        <h2 className="h5 mb-0">Product catalog{data.category ? `: ${data.category}` : ''}</h2>
        <span className="small text-muted">{products.length} product{products.length === 1 ? '' : 's'}</span>
      </div>
      <div className="no-print mb-3">
        <Form.Control type="search" placeholder="Search products" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalog-search" />
        {(data.categories || []).length > 1 && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            <Button size="sm" variant={category === '' ? 'primary' : 'outline-secondary'} onClick={() => setCategory('')}>All</Button>
            {data.categories.map((c) => (
              <Button key={c} size="sm" variant={category === c ? 'primary' : 'outline-secondary'} onClick={() => setCategory(c)}>{c}</Button>
            ))}
          </div>
        )}
      </div>

      {products.length === 0 ? (
        <div className="text-center text-muted py-5">{(data.products || []).length === 0 ? 'No products to show yet.' : 'No products match your search.'}</div>
      ) : (
        <Row className="g-3">
          {products.map((p) => (
            <Col key={p.id} xs={6} md={4} className="catalog-col">
              <div className="catalog-card border rounded h-100 d-flex flex-column overflow-hidden" data-testid="catalog-product">
                {photosOf(p, token).length > 0 ? (
                  <button type="button" className="border-0 p-0 bg-body-secondary" style={{ aspectRatio: '1 / 1', cursor: 'zoom-in' }}
                    onClick={() => { setOpen(p); setShown(0); }} aria-label={`Photos of ${p.name}`}>
                    <img src={photosOf(p, token)[0].thumb} alt={p.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ) : (
                  <div className="bg-body-secondary d-flex align-items-center justify-content-center text-muted" style={{ aspectRatio: '1 / 1' }}>
                    <i className="bi bi-image fs-1" />
                  </div>
                )}
                <div className="p-2 d-flex flex-column flex-grow-1">
                  <div className="fw-semibold">{p.name}</div>
                  <div className="small text-muted">{[p.brand, p.category].filter(Boolean).join(' · ')}</div>
                  {p.description && <div className="small mt-1" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.description}</div>}
                  <div className="mt-auto pt-2">
                    {data.showPrices && (p.price != null ? (
                      <div>
                        <span className="fw-bold">{money(p.price)}</span>{p.unit ? <span className="small text-muted"> / {p.unit}</span> : null}
                        {p.gstRate != null && Number(p.gstRate) > 0 && <div className="small text-muted">+ {Number(p.gstRate)}% GST</div>}
                      </div>
                    ) : <div className="small text-muted">Price on request</div>)}
                    {data.showAvailability && p.inStock !== undefined && (
                      <Badge bg={p.inStock ? 'success' : 'secondary'} className="mt-1">{p.inStock ? 'In stock' : 'Out of stock'}</Badge>
                    )}
                    {p.video && (
                      <div className="no-print mt-2">
                        <a className="btn btn-sm btn-outline-secondary w-100" href={p.video} target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-play-circle me-1" />Watch video
                        </a>
                      </div>
                    )}
                    {phone && (
                      <div className="no-print mt-2">
                        <a className="btn btn-sm btn-outline-success w-100" href={ask(p)} target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-whatsapp me-1" />Ask about this
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
      {data.truncated && <div className="small text-muted mt-3">The first 500 products are shown. Search or choose a category to narrow it down.</div>}

      <Modal show={Boolean(open)} onHide={() => setOpen(null)} centered size="lg">
        {open && (
          <>
            <Modal.Header closeButton><Modal.Title as="h6">{open.name}</Modal.Title></Modal.Header>
            <Modal.Body className="text-center">
              <img src={photosOf(open, token)[shown].full} alt={open.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
              {photosOf(open, token).length > 1 && (
                <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
                  {photosOf(open, token).map((photo, i) => (
                    <button key={photo.id} type="button" onClick={() => setShown(i)} className="p-0 border rounded"
                      style={{ borderWidth: i === shown ? 3 : 1, opacity: i === shown ? 1 : 0.7 }} aria-label={`Photo ${i + 1}`}>
                      <img src={photo.thumb} alt="" width={64} height={64} style={{ objectFit: 'cover' }} />
                    </button>
                  ))}
                </div>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>
    </div>
  );
}

export default CatalogView;
